import { types } from '../../actions/backendConfig';
import { setUiDisplayConfig } from '../../uiConfig';
import type { AbsoluteSilenceInterval, BackendAppConfig } from '../../types';

interface ConfigState {
    pending: boolean;
    app: BackendAppConfig | null;
    bonusScore: number;
}

export const initialState: ConfigState = {
    pending: false,
    app: null,
    bonusScore: 3
};

interface ReducerAction {
    type?: string;
    payload?: Record<string, unknown>;
    error?: unknown;
}

interface ConfigPayload {
    integration?: { enableCallEventsPoller?: boolean };
    saveStream?: boolean;
    realtimeOnlyCallsPercent?: number;
    suggestionCallFrequency?: number;
    recognition?: { system?: { forgetStream?: boolean } };
    callDetection?: {
        useMicrophone?: boolean;
        integrationMethod?: number | null;
        explicitIntegrationSource?: number | null;
        prefixOffset?: number;
        optInGracePeriodMsec?: number;
        explicitPingPeriodMsec?: number;
        appWhitelist?: string[];
    };
    recognitionUseSystem?: boolean;
    enableAnonymization?: boolean;
    enableAsrDebug?: boolean;
    enableCallMetadataOnly?: boolean;
    enableCallTranscriptOnly?: boolean;
    enableRecorder?: boolean;
    logLevel?: string;
    enableRecorderDebug?: boolean;
    enableVoiceEnergyDetection?: boolean;
    recorderMicAudioDeviceName?: string | null;
    recorderSystemAudioDeviceName?: string | null;
    enableCallSimulator?: boolean;
    defaultCallMode?: number;
    disableTrainer?: boolean;
    enableSales?: boolean;
    enableEvaluations?: boolean;
    absoluteSilenceDefinition?: {
        initialThresholdMax?: number;
        initialThresholdMin?: number;
        additionalIntervals?: AbsoluteSilenceInterval[];
        disableDynamicThreshold?: boolean;
        overrideDefaultInitialThreshold?: boolean;
        consecutiveAbsoluteSilenceToEndVoicedChunkMsec?: number;
    };
    userInterface?: {
        displayCallOptOutControls?: boolean;
        displayTranslator?: boolean;
        displayGamificationWeekly?: boolean;
        displayGamificationPerCall?: boolean;
        displayRecordingState?: boolean;
    };
    assistantChat?: {
        enabled?: boolean;
        projectId?: string;
    };
    lowCallVolumeReminder?: {
        enabled?: boolean;
        thresholdCalls?: number;
        periodHours?: number;
    };
}

function isAbsoluteSilenceInterval(value: unknown): value is AbsoluteSilenceInterval {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return false;
    }

    const candidate = value as { threshold?: unknown; minDuration?: unknown };
    return typeof candidate.threshold === 'number' || typeof candidate.minDuration === 'number';
}

interface SetRecordCallPayload {
    recordCall?: boolean;
}

interface ConfigWrapperPayload {
    config?: ConfigPayload;
}

function toSetRecordCallPayload(payload: Record<string, unknown>): SetRecordCallPayload {
    return { recordCall: typeof payload.recordCall === 'boolean' ? payload.recordCall : undefined };
}

function toConfigWrapperPayload(payload: Record<string, unknown>): ConfigWrapperPayload {
    if (typeof payload.config === 'object' && payload.config !== null) {
        return { config: payload.config as ConfigPayload };
    }
    return {};
}

export default function configReducer(state = initialState, { type, payload = {}, error }: ReducerAction = {}) {
    switch (type) {
        case types.UPDATE_APP_CONFIG:
        case types.GET_APP_CONFIG: {
            return {
                ...state,
                pending: true
            };
        }

        case types.SET_RECORD_CALL: {
            const { recordCall } = toSetRecordCallPayload(payload);

            return {
                ...state,
                app: state.app ? ({ ...state.app, recordCall } as BackendAppConfig) : state.app
            };
        }

        case types.UPDATE_APP_CONFIG_SUCCESS:
        case types.GET_APP_CONFIG_SUCCESS: {
            const config = toConfigWrapperPayload(payload).config || {};
            const {
                integration: { enableCallEventsPoller = false } = {},
                saveStream = false,
                realtimeOnlyCallsPercent = 0,
                suggestionCallFrequency = 0,
                callDetection: {
                    useMicrophone = false,
                    integrationMethod = null,
                    explicitIntegrationSource = null,
                    prefixOffset = 0,
                    optInGracePeriodMsec = 0,
                    explicitPingPeriodMsec = 0,
                    appWhitelist = []
                } = {},
                recognitionUseSystem = false,
                enableAnonymization,
                enableAsrDebug,
                enableCallMetadataOnly,
                enableCallTranscriptOnly,
                enableRecorder,
                logLevel = 'warn',
                enableRecorderDebug,
                enableVoiceEnergyDetection,
                recorderMicAudioDeviceName = null,
                recorderSystemAudioDeviceName = null,
                enableCallSimulator,
                defaultCallMode,
                disableTrainer,
                enableSales,
                enableEvaluations,
                assistantChat: { enabled: assistantChatEnabled = false, projectId: assistantChatProjectId = '' } = {},
                absoluteSilenceDefinition: {
                    initialThresholdMax = 0,
                    initialThresholdMin = 0,
                    additionalIntervals = [],
                    disableDynamicThreshold = false,
                    overrideDefaultInitialThreshold = false,
                    consecutiveAbsoluteSilenceToEndVoicedChunkMsec = 0
                } = {},
                userInterface: {
                    displayCallOptOutControls = false,
                    displayTranslator = false,
                    displayGamificationWeekly = false,
                    displayGamificationPerCall = false,
                    displayRecordingState = false
                } = {}
            } = config;
            const forgetStream = config.recognition?.system?.forgetStream ?? false;
            const appConfig: BackendAppConfig = {
                logLevel,
                enableTrainer: !disableTrainer,
                enableSales: enableSales || false,
                enableEvaluations: enableEvaluations || false,
                enableCallSimulator: Boolean(enableCallSimulator),
                saveStream,
                realtimeOnlyCallsPercent,
                suggestionCallFrequency,
                recordCall: true,
                recognition: { system: { forgetStream } },
                callDetection: {
                    useMicrophone,
                    integrationMethod,
                    explicitIntegrationSource,
                    prefixOffset,
                    optInGracePeriodMsec,
                    explicitPingPeriodMsec,
                    appWhitelist,
                    enableCallMetadataOnly: !!enableCallMetadataOnly
                },
                enableCallEventsPoller,
                recognitionUseSystem: Boolean(recognitionUseSystem),
                enableAnonymization: Boolean(enableAnonymization),
                enableCallMetadataOnly: !!enableCallMetadataOnly,
                enableCallTranscriptOnly: !!enableCallTranscriptOnly,
                enableRecorder: !!enableRecorder,
                enableRecorderDebug: !!enableRecorderDebug,
                enableVoiceEnergyDetection: !!enableVoiceEnergyDetection,
                recorderMicAudioDeviceName,
                recorderSystemAudioDeviceName,
                enableAsrDebug: Boolean(enableAsrDebug),
                absoluteSilenceDefinition: {
                    initialThresholdMax,
                    initialThresholdMin,
                    additionalIntervals: Array.isArray(additionalIntervals) ? additionalIntervals.filter(isAbsoluteSilenceInterval) : [],
                    disableDynamicThreshold,
                    overrideDefaultInitialThreshold,
                    consecutiveAbsoluteSilenceToEndVoicedChunkMsec
                },
                assistantChat: {
                    enabled: Boolean(assistantChatEnabled),
                    projectId: String(assistantChatProjectId || '')
                },
                enableManualOptInOptOut: displayCallOptOutControls,
                displayTranslator,
                defaultCallMode: defaultCallMode ?? 0,
                displayGamificationWeekly,
                displayGamificationPerCall,
                displayRecordingState
            };

            setUiDisplayConfig(displayGamificationWeekly, displayRecordingState);
            return {
                ...state,
                app: appConfig,
                pending: false
            };
        }

        case types.UPDATE_APP_CONFIG_FAIL:
        case types.GET_APP_CONFIG_FAIL:
            return {
                ...state,
                pending: false,
                app: null,
                error
            };
        default:
            return state;
    }
}
